import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Input from '../UI/Input'
import Button from '../UI/Button'

const Login = ({ onLogin }) => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState(null)
    const [fieldErrors, setFieldErrors] = useState({})
    const [pending, setPending] = useState(false)
    const [pendingMessage, setPendingMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const next = searchParams.get('next') || '/user-dashboard'

    const validate = () => {
        const errors = {}
        if (!email.trim()) errors.email = 'Email is required.'
        if (!password) errors.password = 'Password is required.'
        return errors
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError(null)
        const errors = validate()
        setFieldErrors(errors)
        if (Object.keys(errors).length > 0) return

        setIsSubmitting(true)
        try {
            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            })

            if (response.status === 403) {
                let data = {}
                const ct = response.headers.get('content-type')
                if (ct && ct.includes('application/json')) {
                    try {
                        data = await response.json()
                    } catch (_) {}
                }
                setPendingMessage(data.error || 'Your access is still pending approval')
                setPending(true)
                return
            }

            if (response.ok) {
                const data = await response.json()
                localStorage.setItem('token', data.token)
                onLogin?.()
                navigate(next)
            } else {
                const contentType = response.headers.get('content-type')
                if (contentType && contentType.includes('application/json')) {
                    const data = await response.json()
                    setError(data.error || 'Login failed.')
                } else {
                    setError('Unexpected server response.')
                }
            }
        } catch (error) {
            setError('Server error. Try again later.')
        } finally {
            setIsSubmitting(false)
        }
    }

    if (pending) {
        return (
            <div className="max-w-md mx-auto bg-bg-light p-8 rounded-lg shadow-md border border-gray-200 mt-10">
                <h2 className="text-3xl font-bold text-center mb-6">Your access is pending approval</h2>
                <p className="text-center">{pendingMessage}</p>
            </div>
        )
    }

    return (
        <div className="max-w-md mx-auto bg-bg-light p-8 rounded-lg shadow-md border border-gray-200 mt-10">
            <h2 className="text-3xl font-bold text-center mb-6">Login</h2>
            <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
                <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    label="Email"
                    error={fieldErrors.email}
                    placeholder="you@example.com"
                    required
                />
                <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    label="Password"
                    error={fieldErrors.password}
                    placeholder="Password"
                    required
                />
                {error && <p className="text-error text-sm">{error}</p>}
                <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
                    Login
                </Button>
            </form>
            <p className="text-center text-text mt-4">
                Don't have an account?{' '}
                <Link to={`/register?next=${encodeURIComponent(next)}`} className="text-primary underline">
                    Register here
                </Link>
            </p>
            <p className="text-center text-text mt-4">
                <Link to="/forgot-password" className="text-blue-500 hover:underline">
                    Forgot Password?
                </Link>
            </p>
        </div>
    )
}

export default Login
